import { useEffect, useState } from 'react';
import { Banner, Button, Dialog, Textarea } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { reportNotFinanceable, type FinancierRecordFields } from './api';
import styles from './financier.module.css';

/**
 * „Als nicht finanzierbar melden" (Finanzierer 08): confirmation with an optional note → `POST
 * /financings/:id/not-financeable`; the financing becomes lost with the reason „Alle Banken abgelehnt".
 */
export function NotFinanceableDialog({
  open,
  financing,
  onClose,
  onDone,
}: {
  open: boolean;
  financing: FinancierRecordFields;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote('');
    setBanner(null);
  }, [open]);

  async function submit() {
    if (pending) return;
    setPending(true);
    setBanner(null);
    try {
      await reportNotFinanceable(financing.id, note.trim() === '' ? null : note.trim());
      onDone();
    } catch (error) {
      setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="danger"
      title="Als nicht finanzierbar melden"
      subtitle={`${financing.numberLabel} · ${financing.linked.contact.name}`}
      footerNote="Grund „Alle Banken abgelehnt“ · Wiederöffnen durch Admin"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button variant="destructiveFilled" loading={pending} onClick={() => void submit()}>
            Als nicht finanzierbar melden
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={styles.muted}>
          Die Finanzierung steht danach auf verloren. Der Immobilien-Deal zeigt einen Hinweis, der Berater
          entscheidet über das weitere Vorgehen.
        </div>
        <Textarea
          label="Notiz"
          optional
          rows={3}
          value={note}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Dialog>
  );
}
